import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { useAdminAuth } from "@/components/AdminLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function AdminChurnRisk() {
  const { admin } = useAdminAuth();
  const [overview, setOverview] = useState(null);
  const [atRiskUsers, setAtRiskUsers] = useState(null);
  const [selectedBand, setSelectedBand] = useState("high");
  const [loading, setLoading] = useState(true);

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem("admin_token")}`,
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const overviewRes = await axios.get(`${API}/admin/churn/overview?limit=50`, { 
          headers: getAuthHeaders() 
        });
        setOverview(overviewRes.data);
      } catch (error) {
        console.error("Failed to fetch churn data:", error);
        toast.error("Failed to load churn risk metrics");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const fetchAtRisk = async () => {
      try {
        const res = await axios.get(`${API}/admin/churn/at-risk?risk_band=${selectedBand}&limit=50`, {
          headers: getAuthHeaders()
        });
        setAtRiskUsers(res.data);
      } catch (error) {
        console.error("Failed to fetch at-risk users:", error);
      }
    };
    if (!loading) {
      fetchAtRisk();
    }
  }, [selectedBand, loading]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const RiskBadge = ({ band }) => {
    const colors = {
      low: "bg-green-100 text-green-800",
      medium: "bg-yellow-100 text-yellow-800",
      high: "bg-red-100 text-red-800",
    };
    return (
      <Badge className={colors[band] || "bg-gray-100"}>
        {band}
      </Badge>
    );
  };

  return (
    <div className="space-y-6" data-testid="admin-churn-risk">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Churn Risk Analysis</h1>
        <p className="text-muted-foreground">Monitor at-risk paying customers</p>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Users at Risk</CardDescription>
            <CardTitle className="text-3xl font-bold text-red-600">
              {overview?.percent_at_risk || 0}%
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Medium or high churn risk
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription>High Risk Users</CardDescription>
            <CardTitle className="text-3xl font-bold text-red-700">
              {overview?.high_risk_users?.length || 0}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Immediate attention needed
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total Evaluated</CardDescription>
            <CardTitle className="text-3xl font-bold">
              {overview?.total_evaluated || 0}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Paying users analyzed
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Risk Distribution by Plan */}
      <Card>
        <CardHeader>
          <CardTitle>Risk Distribution by Plan</CardTitle>
          <CardDescription>Churn risk across subscription tiers</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {Object.entries(overview?.risk_distribution_by_plan || {}).map(([plan, dist]) => (
              <div key={plan} className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="font-medium capitalize">{plan}</span>
                  <span className="text-sm text-muted-foreground">
                    {(dist.low || 0) + (dist.medium || 0) + (dist.high || 0)} users
                  </span>
                </div>
                <div className="flex gap-2 h-6 rounded-lg overflow-hidden">
                  {dist.low > 0 && (
                    <div 
                      className="bg-green-500 flex items-center justify-center text-white text-xs"
                      style={{ flex: dist.low }}
                    >
                      {dist.low}
                    </div>
                  )}
                  {dist.medium > 0 && (
                    <div 
                      className="bg-yellow-500 flex items-center justify-center text-white text-xs"
                      style={{ flex: dist.medium }}
                    >
                      {dist.medium}
                    </div>
                  )}
                  {dist.high > 0 && (
                    <div 
                      className="bg-red-500 flex items-center justify-center text-white text-xs"
                      style={{ flex: dist.high }}
                    >
                      {dist.high}
                    </div>
                  )}
                </div>
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span className="text-green-600">Low: {dist.low || 0}</span>
                  <span className="text-yellow-600">Medium: {dist.medium || 0}</span>
                  <span className="text-red-600">High: {dist.high || 0}</span>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* At-Risk Users Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>At-Risk Users</CardTitle>
              <CardDescription>Filter by risk band</CardDescription>
            </div>
            <Select value={selectedBand} onValueChange={setSelectedBand}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="high">High Risk</SelectItem>
                <SelectItem value="medium">Medium Risk</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User ID</TableHead>
                <TableHead>Plan</TableHead>
                <TableHead>Days Inactive</TableHead>
                <TableHead>Usage Drop</TableHead>
                <TableHead>Risk Score</TableHead>
                <TableHead>Band</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(atRiskUsers?.users || []).map((user) => (
                <TableRow key={user.user_id}>
                  <TableCell className="font-mono text-xs">
                    {user.user_id?.slice(0, 8)}...
                  </TableCell>
                  <TableCell className="capitalize">{user.plan_type || 'unknown'}</TableCell>
                  <TableCell>
                    <span className={user.days_since_last_activity > 14 ? 'text-red-600 font-medium' : ''}>
                      {user.days_since_last_activity} days
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className={user.credit_velocity_drop_pct > 50 ? 'text-red-600 font-medium' : ''}>
                      {user.credit_velocity_drop_pct?.toFixed(1)}%
                    </span>
                  </TableCell>
                  <TableCell className="font-bold">{user.churn_risk_score}</TableCell>
                  <TableCell>
                    <RiskBadge band={user.risk_band} />
                  </TableCell>
                </TableRow>
              ))}
              {(atRiskUsers?.users || []).length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground py-4">
                    No users in this risk band
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* High Risk Users Detail */}
      {overview?.high_risk_users?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-red-700">High Priority: Immediate Attention</CardTitle>
            <CardDescription>These users have the highest churn risk scores</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {overview.high_risk_users.slice(0, 5).map((user) => (
                <div key={user.user_id} className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-mono text-sm">{user.user_id?.slice(0, 12)}...</span>
                    <Badge className="bg-red-600 text-white">Score: {user.churn_risk_score}</Badge>
                  </div>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground">Plan</p>
                      <p className="font-medium capitalize">{user.plan_type}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Inactive</p>
                      <p className="font-medium">{user.metrics?.days_since_last_activity || 0} days</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Usage Drop</p>
                      <p className="font-medium">{user.metrics?.credit_velocity_drop_pct?.toFixed(1) || 0}%</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
